import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, ShieldCheck, RefreshCw } from "lucide-react";
import { api, imgUrl, CATEGORY_OPTIONS } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const CATEGORY_IMAGES = {
  skincare: "https://images.pexels.com/photos/6800936/pexels-photo-6800936.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=700&w=600",
  haircare: "https://images.unsplash.com/photo-1660090455967-24cf8b0eb58d?w=600",
  fragrance: "https://images.pexels.com/photos/34690231/pexels-photo-34690231.jpeg?w=600",
  "bath-body": "https://images.unsplash.com/photo-1678799854106-89d81e74f17a?w=600",
  jewellery: "https://images.pexels.com/photos/32780784/pexels-photo-32780784.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=700&w=600",
  accessories: "https://images.pexels.com/photos/36557821/pexels-photo-36557821.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=700&w=600",
};

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 8)));
  }, []);

  return (
    <div className="animate-fade-up">
      {/* Hero */}
      <section className="relative">
        <div className="grid md:grid-cols-2">
          <div className="flex flex-col justify-center px-4 py-16 md:px-12 lg:px-20 lg:py-28">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="label-eyebrow"
            >
              KK Beauty &amp; Fashion
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-display mt-4 text-4xl font-light leading-[1.05] tracking-tight text-zinc-900 sm:text-5xl lg:text-6xl"
            >
              Your everyday beauty <br /> and style essentials
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6 max-w-md text-base leading-relaxed text-zinc-500"
            >
              All in one place — curated skincare, fragrance, jewellery and accessories.
              Easy to choose, easy to shop.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-8 flex gap-3"
            >
              <Link
                to="/shop"
                className="group flex h-12 items-center gap-2 bg-zinc-900 px-8 text-xs font-medium uppercase tracking-widest text-white transition-colors hover:bg-zinc-800"
                data-testid="hero-shop-btn"
              >
                Shop Now
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/category/jewellery"
                className="flex h-12 items-center border border-zinc-900 px-8 text-xs font-medium uppercase tracking-widest text-zinc-900 transition-colors hover:bg-zinc-50"
              >
                Jewellery
              </Link>
            </motion.div>
          </div>
          <div className="zoom-wrap relative h-72 bg-zinc-100 md:h-auto">
            <img
              src="https://images.pexels.com/photos/8101521/pexels-photo-8101521.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
              alt="Premium beauty essentials"
              className="zoom-img h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-zinc-200 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-zinc-200 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          {[
            { icon: Truck, t: "Free Shipping", s: "On orders over $75" },
            { icon: ShieldCheck, t: "Secure Checkout", s: "Encrypted payments" },
            { icon: RefreshCw, t: "Easy Returns", s: "30-day guarantee" },
          ].map((f) => (
            <div key={f.t} className="flex items-center gap-3 px-6 py-6 md:justify-center">
              <f.icon size={20} className="text-kkaccent" />
              <div>
                <p className="text-sm font-medium">{f.t}</p>
                <p className="text-xs text-zinc-500">{f.s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-4 py-20 md:px-8">
        <div className="mb-10 flex items-end justify-between">
          <div>
            <p className="label-eyebrow">Shop by</p>
            <h2 className="font-display mt-2 text-3xl font-light tracking-tight md:text-4xl">Categories</h2>
          </div>
          <Link to="/shop" className="hidden text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-900 sm:block">
            View all
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
          {CATEGORY_OPTIONS.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="zoom-wrap group relative aspect-[4/5] bg-zinc-100"
              data-testid={`home-category-${c.slug}`}
            >
              <img src={imgUrl(CATEGORY_IMAGES[c.slug])} alt={c.name} className="zoom-img h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 p-5">
                <h3 className="font-display text-xl font-normal text-white">{c.name}</h3>
                <span className="mt-1 inline-flex items-center gap-1 text-xs uppercase tracking-wider text-white/80">
                  Explore <ArrowRight size={12} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="mx-auto max-w-7xl px-4 pb-20 md:px-8">
        <div className="mb-10">
          <p className="label-eyebrow">Top Picks</p>
          <h2 className="font-display mt-2 text-3xl font-light tracking-tight md:text-4xl">New &amp; Trending</h2>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-3 lg:grid-cols-4">
          {featured.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* Editorial banner */}
      <section className="relative">
        <div className="zoom-wrap relative h-[420px]">
          <img
            src="https://images.pexels.com/photos/34362936/pexels-photo-34362936.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
            alt="Fashion editorial"
            className="zoom-img h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute inset-0 mx-auto flex max-w-7xl flex-col justify-center px-4 md:px-8">
            <p className="text-xs uppercase tracking-[0.3em] text-white/80">Pure &amp; Simple</p>
            <h2 className="font-display mt-3 max-w-md text-3xl font-light leading-tight text-white md:text-4xl">
              Care that keeps you looking your best
            </h2>
            <Link
              to="/shop"
              className="mt-6 inline-flex w-fit items-center gap-2 bg-white px-8 py-3 text-xs font-medium uppercase tracking-widest text-zinc-900 hover:bg-zinc-100"
            >
              Discover <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
